/**
 * Historial desde el principio: lee un extracto entero para empezar una
 * cuenta con él (travesía). A diferencia de «Importar extracto», aquí no se
 * pregunta por las columnas: si no se reconocen, se avisa y se puede
 * escribir el saldo a mano.
 */
import { amountToInteger } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { ImportTransactionEntity } from '@actual-app/core/types/models';

import { nombreArchivo } from '#components/mobile/importar/importar';
import { leerArchivo } from '#components/mobile/importar/leerArchivo';
import type { ErrorLectura } from '#components/mobile/importar/leerArchivo';
import { movimientosDeFilas } from '#components/mobile/importar/leerExtracto';
import type { MovimientoLeido } from '#components/mobile/importar/leerExtracto';

export type ExtractoLeido =
  | {
      tipo: 'ok';
      archivo: string;
      movimientos: MovimientoLeido[];
      /** Saldo final del extracto en céntimos, si lo trae. */
      saldoFinal: IntegerAmount | null;
    }
  | { tipo: 'error'; error: ErrorLectura };

export async function leerExtractoHistoria(
  ruta: string,
): Promise<ExtractoLeido> {
  const leido = await leerArchivo(ruta);
  if (leido.tipo === 'error') {
    return leido;
  }
  const archivo = nombreArchivo(ruta);
  if (leido.tipo === 'estructurado') {
    return {
      tipo: 'ok',
      archivo,
      movimientos: leido.movimientos,
      saldoFinal:
        leido.saldoFinal == null ? null : amountToInteger(leido.saldoFinal),
    };
  }
  if (!leido.lectura) {
    return { tipo: 'error', error: 'vacio' };
  }
  const { movimientos, saldoFinal } = movimientosDeFilas(
    leido.filas,
    leido.lectura,
  );
  if (movimientos.length === 0) {
    return { tipo: 'error', error: 'vacio' };
  }
  return {
    tipo: 'ok',
    archivo,
    movimientos,
    saldoFinal: saldoFinal == null ? null : amountToInteger(saldoFinal),
  };
}

/** Movimientos leídos → movimientos para `transactions-import`. */
export function paraImportar(
  movimientos: readonly MovimientoLeido[],
): ImportTransactionEntity[] {
  return movimientos.map(m => ({
    account: '',
    date: m.fecha,
    amount: amountToInteger(m.importe),
    payee_name: m.concepto,
    imported_payee: m.concepto,
    ...(m.idBanco ? { imported_id: m.idBanco } : null),
    cleared: true,
  }));
}
